import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  TextField,
  Button,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Dialog,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton
} from '@mui/material';
import { APIProvider, Map, AdvancedMarker, Pin, InfoWindow } from '@vis.gl/react-google-maps';
import { useJsApiLoader } from '@react-google-maps/api';
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import axios from '../utils/axios';
import { formatPrice, getCurrencySymbol } from '../utils/currency';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SearchIcon from '@mui/icons-material/Search';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import MapIcon from '@mui/icons-material/Map';
import ListIcon from '@mui/icons-material/List';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const LIBRARIES = ['places', 'geometry'];

const GymList = () => {
  const [gyms, setGyms] = useState([]);
  const [filteredGyms, setFilteredGyms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [searchAddress, setSearchAddress] = useState('');
  const [searchLocation, setSearchLocation] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'map'
  const [mapDialogOpen, setMapDialogOpen] = useState(false);
  const [selectedGym, setSelectedGym] = useState(null);
  const [gymCount, setGymCount] = useState(0);
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [nearbyGyms, setNearbyGyms] = useState([]);
  const [infoWindowOpen, setInfoWindowOpen] = useState(true);
  const mapRef = useRef(null);
  const navigate = useNavigate();

  // Load Google Maps API for PlacesAutocomplete
  const { isLoaded: isGoogleMapsLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES
  });

  const mapContainerStyle = {
    width: '100%',
    height: '100%',
    borderRadius: '8px'
  };

  const mapOptions = {
    zoom: 18,
    mapTypeControl: true,
    streetViewControl: true,
    fullscreenControl: true,
    zoomControl: true,
    gestureHandling: 'greedy',
    disableDefaultUI: false,
    styles: [
      {
        featureType: 'poi',
        elementType: 'labels',
        stylers: [{ visibility: 'on' }]
      },
      {
        featureType: 'poi.business',
        elementType: 'labels',
        stylers: [{ visibility: 'on' }]
      },
      // Hide other business labels to make our gym stand out
      {
        featureType: 'poi.business',
        elementType: 'labels.text',
        stylers: [{ visibility: 'off' }]
      }
    ]
  };

  // Calculate distance between two points in km
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    // Validate input parameters
    if (typeof lat1 !== 'number' || typeof lon1 !== 'number' || 
        typeof lat2 !== 'number' || typeof lon2 !== 'number' ||
        isNaN(lat1) || isNaN(lon1) || isNaN(lat2) || isNaN(lon2)) {
      return 999999; // Return large distance for invalid coordinates
    }
    
    const R = 6371; // Earth radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c; // Distance in km
  };

  // Get user's current location
  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          // Validate position object
          if (!position || !position.coords) {
            console.warn('Invalid position object received:', position);
            setLocationError('Invalid location data received. Please search manually.');
            return;
          }
          
          const lat = position.coords?.latitude;
          const lng = position.coords?.longitude;
          
          // Validate coordinates exist and are numbers
          if (lat === undefined || lng === undefined || 
              typeof lat !== 'number' || typeof lng !== 'number' || 
              isNaN(lat) || isNaN(lng) || 
              lat < -90 || lat > 90 || lng < -180 || lng > 180) {
            console.warn('Invalid coordinates received:', { lat, lng });
            setLocationError('Invalid location coordinates received. Please search manually.');
            return;
          }
          
          const location = { lat, lng };
          setUserLocation(location);
          setSearchLocation(location);
          setLocationError(null); // Clear any previous location errors
          
          // Reverse geocode to get address (only if Google Maps is available)
          if (isGoogleMapsLoaded && window.google && window.google.maps) {
            try {
              const geocoder = new window.google.maps.Geocoder();
              geocoder.geocode({ location }, (results, status) => {
                if (status === 'OK' && results[0]) {
                  setSearchAddress(results[0].formatted_address);
                }
              });
            } catch (error) {
              console.warn('Geocoding failed:', error);
            }
          }
        },
        (error) => {
          console.error('Error getting location:', error);
          setLocationError('Could not get your location. Please search manually.');
        }
      );
    } else {
      setLocationError('Geolocation is not supported by your browser.');
    }
  };

  // Handle address selection from autocomplete
  const handleAddressSelect = async (address) => {
    try {
      setSearchAddress(address);
      const results = await geocodeByAddress(address);
      if (results && results.length > 0) {
        const latLng = await getLatLng(results[0]);
        if (latLng && typeof latLng.lat === 'number' && typeof latLng.lng === 'number') {
          setSearchLocation(latLng);
          setLocationError(null); // Clear location error when user manually searches
        } else {
          throw new Error('Invalid coordinates returned from geocoding');
        }
      } else {
        throw new Error('No results found for this address');
      }
    } catch (error) {
      console.error('Error getting address details:', error);
      setLocationError('Could not find location for this address. Please enter a valid address.');
    }
  };

  // Handle marker click
  const handleMarkerClick = () => {
    setInfoWindowOpen(!infoWindowOpen);
  };

  const handleInfoWindowClose = () => {
    setInfoWindowOpen(false);
  };

  // Fetch gyms and sort by distance
  useEffect(() => {
    const fetchGyms = async () => {
      try {
        setLoading(true);
        setError(null); // Clear any previous errors
        const response = await axios.get('/api/gyms');
        
        // The API returns an array directly
        const gymsData = Array.isArray(response.data) ? response.data : [];
        
        console.log('[GymList] Fetched', gymsData.length, 'gyms from API');
        
        setGyms(gymsData);
        setFilteredGyms(gymsData);
        setLoading(false);
      } catch (err) {
        console.error('[GymList] Error fetching gyms:', err);
        setError('Failed to fetch gyms. Please try again later.');
        setGyms([]);
        setFilteredGyms([]);
        setLoading(false);
      }
    };

    fetchGyms();
    
    // Get user location on component mount (non-blocking)
    getUserLocation();
  }, []);

  // Filter and sort gyms based on location
  useEffect(() => {
    if (searchLocation && gyms.length > 0 && searchLocation.lat && searchLocation.lng) {
      const gymsWithDistance = gyms.map(gym => {
        const gymLocation = gym.location?.coordinates;
        // Only calculate distance if gym has valid coordinates
        if (gymLocation && Array.isArray(gymLocation) && gymLocation.length >= 2) {
          const distance = calculateDistance(
            searchLocation.lat, 
            searchLocation.lng,
            gymLocation[1], 
            gymLocation[0]
          );
          return { ...gym, distance };
        } else {
          // If no valid coordinates, assign a large distance to sort to the end
          return { ...gym, distance: 999999 };
        }
      });
      
      // Sort by distance
      const sortedGyms = [...gymsWithDistance].sort((a, b) => a.distance - b.distance);
      setFilteredGyms(sortedGyms);
    } else {
      // If no location, show all gyms
      setFilteredGyms(gyms);
    }
  }, [searchLocation, gyms]);

  // Get nearby gyms within 40km for map view
  useEffect(() => {
    if (searchLocation && filteredGyms.length > 0 && searchLocation.lat && searchLocation.lng) {
      const nearby = filteredGyms.filter(gym => gym.distance && gym.distance <= 40);
      setNearbyGyms(nearby);
      setGymCount(nearby.length);
      
      // Auto-select the first gym if available
      if (nearby.length > 0 && !selectedGym) {
        setSelectedGym(nearby[0]);
      }
    } else {
      setNearbyGyms([]);
      setGymCount(0);
    }
  }, [searchLocation, filteredGyms]);

  const handleViewGym = (gymId) => {
    navigate(`/gyms/${gymId}`);
  };

  const toggleViewMode = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
      if (newViewMode === 'map') {
        setMapDialogOpen(true);
      }
    }
  };

  const handleSelectGym = (gym) => {
    setSelectedGym(gym);
    setInfoWindowOpen(true); // Show info window when selecting a gym
  };

  const handleMapLoad = (map) => {
    if (nearbyGyms.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      
      // Add user location to bounds
      if (searchLocation) {
        bounds.extend(searchLocation);
      }
      
      // Add all gym locations to bounds
      nearbyGyms.forEach(gym => {
        if (gym.location && gym.location.coordinates && Array.isArray(gym.location.coordinates) && gym.location.coordinates.length >= 2) {
          bounds.extend({
            lat: gym.location.coordinates[1],
            lng: gym.location.coordinates[0]
          });
        }
      });
      
      // Fit map to bounds with some padding, but don't restrict zoom after that
      map.fitBounds(bounds);
    }
  };

  const toggleSidebar = () => {
    setSidebarVisible(!sidebarVisible);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  // Only show critical errors (like failed API calls) that block the page
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (gyms.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="info">No gyms available at the moment. Please check back later.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Available Gyms
        </Typography>
        
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={toggleViewMode}
          aria-label="view mode"
        >
          <ToggleButton value="list" aria-label="list view">
            <ListIcon sx={{ mr: 1 }} /> List
          </ToggleButton>
          <ToggleButton value="map" aria-label="map view">
            <MapIcon sx={{ mr: 1 }} /> Map
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Location Error Alert (non-blocking) */}
      {locationError && (
        <Alert 
          severity="warning" 
          onClose={() => setLocationError(null)}
          sx={{ mb: 2 }}
        >
          {locationError}
        </Alert>
      )}

      {/* Search Bar */}
      <Box sx={{ mb: 4, display: 'flex', gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          {isGoogleMapsLoaded && process.env.REACT_APP_GOOGLE_MAPS_API_KEY ? (
            <PlacesAutocomplete
              value={searchAddress}
              onChange={setSearchAddress}
              onSelect={handleAddressSelect}
            >
              {({ getInputProps, suggestions, getSuggestionItemProps }) => (
                <div>
                  <TextField
                    {...getInputProps({
                      fullWidth: true,
                      label: 'Search gyms near...',
                      placeholder: 'Enter an address or city',
                      variant: 'outlined',
                      InputProps: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }
                    })}
                  />
                  <Box sx={{ position: 'relative' }}>
                    <Box sx={{
                      position: 'absolute',
                      zIndex: 1000,
                      width: '100%',
                      bgcolor: 'background.paper',
                      boxShadow: 3,
                      borderRadius: 1,
                      mt: 0.5,
                      maxHeight: 200,
                      overflowY: 'auto'
                    }}>
                      {suggestions.map(suggestion => {
                        const style = {
                          backgroundColor: suggestion.active ? '#f0f0f0' : '#fff',
                          cursor: 'pointer',
                          padding: '10px',
                          borderBottom: '1px solid #eee'
                        };
                        return (
                          <div
                            {...getSuggestionItemProps(suggestion, { style })}
                            key={suggestion.placeId}
                          >
                            {suggestion.description}
                          </div>
                        );
                      })}
                    </Box>
                  </Box>
                </div>
              )}
            </PlacesAutocomplete>
          ) : (
            <TextField
              fullWidth
              label="Search gyms near..."
              value={searchAddress}
              onChange={(e) => setSearchAddress(e.target.value)}
              placeholder="Enter an address or city"
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          )}
        </Box>
        
        <Button
          variant="outlined"
          startIcon={<MyLocationIcon />}
          onClick={getUserLocation}
          sx={{ height: '56px' }}
        >
          Use My Location
        </Button>
      </Box>

      {/* Distance indicator */}
      {searchLocation && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1">
            Showing gyms near: <strong>{searchAddress || 'Your location'}</strong>
          </Typography>
        </Box>
      )}

      {/* Gym List */}
      {viewMode === 'list' && (
        <Grid container spacing={3}>
          {filteredGyms.map((gym) => (
            <Grid item xs={12} sm={6} md={4} key={gym._id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    transition: 'transform 0.2s ease-in-out',
                    cursor: 'pointer',
                  },
                }}
                onClick={() => handleViewGym(gym._id)}
              >
                <CardMedia
                  component="img"
                  height="200"
                  image={gym.photos[0] || 'https://via.placeholder.com/400x200?text=No+Image+Available'}
                  alt={gym.name}
                  sx={{ objectFit: 'cover' }}
                />
                <CardContent>
                  <Typography gutterBottom variant="h5" component="h2">
                    {gym.name}
                  </Typography>
                  
                  {/* Distance */}
                  {gym.distance !== undefined && (
                    <Typography variant="body2" color="primary" sx={{ mb: 1 }}>
                      {gym.distance.toFixed(1)} km away
                    </Typography>
                  )}
                  
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      mb: 2
                    }}
                  >
                    {gym.description}
                  </Typography>

                  {/* Amenities */}
                  {gym.amenities && Object.keys(gym.amenities).some(key => gym.amenities[key]) && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Amenities:
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {Object.entries(gym.amenities).map(([key, value]) => {
                          const boolValue = typeof value === 'string' ? value === 'true' : Boolean(value);
                          return boolValue && (
                            <Typography
                              key={key}
                              variant="caption"
                              sx={{
                                bgcolor: 'primary.main',
                                color: 'white',
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                fontSize: '0.7rem'
                              }}
                            >
                              {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                            </Typography>
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                  
                  {/* Address Section */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start',
                    mb: 2,
                    p: 1.5,
                    bgcolor: '#f9f9f9',
                    borderRadius: 1
                  }}>
                    <LocationOnIcon 
                      color="primary" 
                      fontSize="small" 
                      sx={{ mr: 1, mt: 0.5 }} 
                    />
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ flex: 1 }}
                    >
                      {gym.address}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" color="primary">
                      {formatPrice(gym.pricePerHour, gym.currency)}/hr
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Max {gym.maxPeople} people
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Map View Dialog */}
      <Dialog
        open={mapDialogOpen}
        onClose={() => setMapDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        sx={{ '& .MuiDialog-paper': { overflow: 'hidden' } }}
      >
        <DialogContent sx={{ p: 0, position: 'relative', height: '80vh' }}>
          <Box sx={{ 
            position: 'absolute', 
            top: 16, 
            right: 16, 
            zIndex: 1000,
            display: 'flex',
            gap: 1
          }}>
            <IconButton
              onClick={toggleSidebar}
              sx={{ 
                bgcolor: 'rgba(255, 255, 255, 0.9)', 
                boxShadow: 3,
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 1)'
                }
              }}
            >
              {sidebarVisible ? <ArrowBackIcon /> : <ArrowForwardIcon />}
            </IconButton>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => setMapDialogOpen(false)}
              sx={{ fontWeight: 'bold' }}
            >
              Close
            </Button>
          </Box>
          
          <Box sx={{ 
            position: 'absolute', 
            top: 16, 
            left: 16, 
            zIndex: 1000, 
            bgcolor: 'rgba(255, 255, 255, 0.9)', 
            p: 2, 
            borderRadius: 1,
            boxShadow: 3,
            minWidth: 200
          }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
              Gyms Nearby
            </Typography>
            <Typography variant="body1" color="primary" sx={{ fontWeight: 'bold' }}>
              {gymCount} gyms within 40km
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', height: '100%' }}>
            {sidebarVisible && (
              <Box sx={{ 
                width: 350, 
                height: '100%', 
                overflowY: 'auto',
                bgcolor: 'background.paper',
                borderRight: '1px solid #e0e0e0',
                zIndex: 1,
                boxShadow: 3
              }}>
                <Box sx={{ p: 2, pt: 8 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Nearby Gyms
                  </Typography>
                  <List>
                    {nearbyGyms.map((gym) => (
                      <React.Fragment key={gym._id}>
                        <ListItem 
                          button 
                          selected={selectedGym?._id === gym._id}
                          onClick={() => handleSelectGym(gym)}
                          sx={{
                            mb: 1,
                            borderRadius: 1,
                            bgcolor: selectedGym?._id === gym._id ? '#e3f2fd' : 'transparent',
                            '&:hover': {
                              bgcolor: selectedGym?._id === gym._id ? '#e3f2fd' : '#f5f5f5'
                            }
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                  {gym.name}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewGym(gym._id);
                                  }}
                                  sx={{ color: 'primary.main' }}
                                >
                                  <OpenInNewIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            }
                            secondary={
                              <>
                                <Typography variant="body2" color="text.secondary">
                                  {gym.distance.toFixed(1)} km away
                                </Typography>
                                <Typography variant="body2" color="text.secondary" noWrap>
                                  {gym.address}
                                </Typography>
                              </>
                            }
                          />
                        </ListItem>
                        <Divider sx={{ my: 1 }} />
                      </React.Fragment>
                    ))}
                  </List>
                  
                  {gymCount === 0 && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      No gyms found within 40km of your location.
                    </Alert>
                  )}
                </Box>
              </Box>
            )}
            
            <Box sx={{ flex: 1, position: 'relative' }}>
              {!process.env.REACT_APP_GOOGLE_MAPS_API_KEY ? (
                <Box sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  bgcolor: '#f5f5f5'
                }}>
                  <Typography variant="body2" color="error">
                    Google Maps API key is missing
                  </Typography>
                </Box>
              ) : !searchLocation ? (
                <Box sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  bgcolor: '#f5f5f5'
                }}>
                  <Typography variant="body2">
                    Location not available
                  </Typography>
                </Box>
              ) : (
                <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY} libraries={LIBRARIES}>
                  <Map
                    mapId="gym-location-map"
                    defaultCenter={searchLocation}
                    center={searchLocation}
                    defaultZoom={12}
                    style={mapContainerStyle}
                    gestureHandling="greedy"
                    disableDefaultUI={false}
                    onLoad={handleMapLoad}
                  >
                    {/* User location marker */}
                    <AdvancedMarker 
                      position={searchLocation}
                    >
                      <Pin
                        background={'#34A853'}
                        borderColor={'#FFFFFF'}
                        glyphColor={'#FFFFFF'}
                        scale={1.2}
                      />
                    </AdvancedMarker>
                    
                    {/* All nearby gym markers */}
                    {nearbyGyms.map((gym) => {
                      if (!gym.location || !gym.location.coordinates || !Array.isArray(gym.location.coordinates) || gym.location.coordinates.length < 2) {
                        return null;
                      }
                      
                      const isSelected = selectedGym?._id === gym._id;
                      
                      return (
                        <AdvancedMarker 
                          key={gym._id}
                          position={{
                            lat: gym.location.coordinates[1], 
                            lng: gym.location.coordinates[0]
                          }}
                          onClick={() => handleSelectGym(gym)}
                        >
                          <Pin
                            background={isSelected ? '#FF6B35' : '#1976d2'}
                            borderColor={'#FFFFFF'}
                            glyphColor={'#FFFFFF'}
                            scale={isSelected ? 1.2 : 1.0}
                          />
                        </AdvancedMarker>
                      );
                    })}
                    
                    {/* Selected gym info window */}
                    {selectedGym && selectedGym.location && selectedGym.location.coordinates && Array.isArray(selectedGym.location.coordinates) && selectedGym.location.coordinates.length >= 2 && (
                      <>
                        {infoWindowOpen && (
                          <InfoWindow
                            position={{
                              lat: selectedGym.location.coordinates[1] + 0.0001,
                              lng: selectedGym.location.coordinates[0]
                            }}
                            pixelOffset={{ x: 0, y: -40 }}
                            onCloseClick={handleInfoWindowClose}
                          >
                            <Box sx={{ 
                              p: 1.5, 
                              minWidth: 200,
                              maxWidth: 300,
                              textAlign: 'center',
                              background: 'linear-gradient(135deg, #FF6B35 0%, #F7931E 100%)',
                              borderRadius: '8px',
                              color: 'white',
                              boxShadow: '0 4px 12px rgba(255, 107, 53, 0.3)'
                            }}>
                              <Typography 
                                variant="h6" 
                                sx={{ 
                                  fontWeight: 'bold',
                                  fontSize: '1.2rem',
                                  textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                                  mb: 0.5,
                                  lineHeight: 1.2
                                }}
                              >
                                🏋️ {selectedGym.name}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontSize: '0.9rem',
                                  opacity: 0.95,
                                  fontWeight: 500,
                                  mt: 0.5
                                }}
                              >
                                {formatPrice(selectedGym.pricePerHour, selectedGym.currency)}/hr • Max {selectedGym.maxPeople} people
                              </Typography>
                            </Box>
                          </InfoWindow>
                        )}
                      </>
                    )}
                  </Map>
                </APIProvider>
              )}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default GymList;
